/**
 * Punkt 121 (Entscheidung Antoine: „Formularnamen im System pflegbar“) — Reiter „Anfrage-Eingänge“
 * unter Einstellungen. Je Eingang (`inquiries.source_detail`) ein Titel, die Website und ob er
 * aktiv ist. Die Liste zeigt auch Eingänge, die in Anfragen vorkommen, aber noch keinen Titel
 * haben — dort steht dann der rohe Kennzeichner, bis jemand ihn benennt.
 */
import { useState } from "react";
import { useApi, apiMutate } from "../hooks/useApi";
import { Button, Card, EmptyState, ErrorNote, Input, LoadingRows, Textarea, Toggle } from "../ui/components";

export interface AnfrageEingang {
  sourceDetail: string;
  id: string | null;
  titel: string | null;
  website: string | null;
  aktiv: boolean;
  anzahl: number;
}

function EingangZeile({ e, canEdit, onSaved }: { e: AnfrageEingang; canEdit: boolean; onSaved: () => void }) {
  const [titel, setTitel] = useState(e.titel ?? "");
  const [website, setWebsite] = useState(e.website ?? "");
  const [aktiv, setAktiv] = useState(e.aktiv);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const geaendert = titel !== (e.titel ?? "") || website !== (e.website ?? "") || aktiv !== e.aktiv;

  async function speichern() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate("/api/anfrage-eingaenge", "PUT", {
        sourceDetail: e.sourceDetail,
        titel: titel.trim(),
        website: website.trim() || null,
        aktiv,
      });
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li data-testid="eingang-pflege-zeile" className="grid grid-cols-1 gap-2 py-4 md:grid-cols-[1fr_14rem_auto_auto] md:items-center md:gap-4">
      <div className="min-w-0">
        <Input aria-label={`Titel für ${e.sourceDetail}`} placeholder="Titel, z. B. Silvester-Anfrage" maxLength={200} value={titel} disabled={!canEdit} onChange={(ev) => setTitel(ev.target.value)} />
        <p className="mt-1 truncate font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
          {e.sourceDetail} · {e.anzahl === 1 ? "1 Anfrage" : `${e.anzahl} Anfragen`}
        </p>
      </div>
      <Input aria-label={`Website für ${e.sourceDetail}`} placeholder="Website" maxLength={200} value={website} disabled={!canEdit} onChange={(ev) => setWebsite(ev.target.value)} />
      <Toggle checked={aktiv} onChange={setAktiv} label={`${e.sourceDetail} aktiv`} disabled={!canEdit} />
      {canEdit && (
        <Button type="button" variant="secondary" disabled={busy || !titel.trim() || (!geaendert && e.id !== null)} onClick={() => void speichern()}>
          {busy ? "Speichert …" : "Speichern"}
        </Button>
      )}
      {fehler && <ErrorNote message={fehler} />}
    </li>
  );
}

export function EingaengeTab({ canEdit }: { canEdit: boolean }) {
  const { data, loading, error, reload } = useApi<AnfrageEingang[]>("/api/anfrage-eingaenge");
  return (
    <Card className="p-6">
      <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">Anfrage-Eingänge</h2>
      <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Über welches Formular eine Anfrage kam, steht in der Anfragenliste, am Vorgang und in der Auswertung — mit dem Titel, den du hier vergibst.
      </p>
      {loading && <LoadingRows rows={4} />}
      {error && <ErrorNote message={error} onRetry={reload} />}
      {data && data.length === 0 && (
        <EmptyState icon="input" title="Noch keine Eingänge" hint="Sobald eine Anfrage über ein Formular eingeht, erscheint ihr Eingang hier." />
      )}
      {data && data.length > 0 && (
        <ul className="mt-4 divide-y divide-outline-variant/30">
          {data.map((e) => (
            <EingangZeile key={`${e.sourceDetail}:${e.titel ?? ""}:${e.website ?? ""}:${e.aktiv}`} e={e} canEdit={canEdit} onSaved={reload} />
          ))}
        </ul>
      )}
    </Card>
  );
}

/**
 * Interne Absender (Migration 719): Anfragen, deren Kundenname, Firma oder E-Mail einen dieser
 * Einträge enthält, zählen in der Auswertung nicht mit — wie Testanfragen. Ein Eintrag je Zeile;
 * die Liste wird als Ganzes über PUT /api/settings/payment-terms gespeichert.
 */
function InterneAbsenderFormular({ liste, canEdit, onSaved }: { liste: string[]; canEdit: boolean; onSaved: () => void }) {
  const [text, setText] = useState(liste.join("\n"));
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const neu = text.split("\n").map((z) => z.trim()).filter((z) => z !== "");
  const geaendert = neu.join("\n") !== liste.join("\n");
  const zuKurz = neu.filter((z) => z.length < 4);

  async function speichern() {
    setBusy(true);
    setFehler(null);
    try {
      await apiMutate("/api/settings/payment-terms", "PUT", { interneAbsender: neu });
      onSaved();
    } catch (err) {
      setFehler(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-4 space-y-3">
      <Textarea
        aria-label="Interne Absender"
        data-testid="interne-absender-feld"
        rows={5}
        placeholder={"Ein Eintrag je Zeile, z. B.\nMax Muster\nMuster GmbH\nteam@muster.example"}
        value={text}
        disabled={!canEdit}
        onChange={(ev) => setText(ev.target.value)}
      />
      {canEdit && (
        <Button type="button" variant="secondary" disabled={busy || !geaendert || zuKurz.length > 0} onClick={() => void speichern()}>
          {busy ? "Speichert …" : "Speichern"}
        </Button>
      )}
      {zuKurz.length > 0 && <ErrorNote message={`Mindestens 4 Zeichen je Eintrag: ${zuKurz.join(", ")}`} />}
      {fehler && <ErrorNote message={fehler} />}
    </div>
  );
}

export function InterneAbsenderKarte({ canEdit }: { canEdit: boolean }) {
  const { data, loading, error, reload } = useApi<{ interneAbsender?: string[] } | null>("/api/settings/payment-terms");
  const liste = data?.interneAbsender ?? [];
  return (
    <Card className="p-6">
      <h2 className="font-headline-sm text-headline-sm font-bold text-on-background dark:text-inverse-on-surface">Interne Absender</h2>
      <p className="mt-1 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
        Anfragen dieser Personen oder Firmen zählen nicht in der Auswertung.
      </p>
      {loading && !data && <LoadingRows rows={2} />}
      {error && <ErrorNote message={error} onRetry={reload} />}
      {data && <InterneAbsenderFormular key={liste.join("\n")} liste={liste} canEdit={canEdit} onSaved={reload} />}
    </Card>
  );
}
